import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./capTable.css";
import { buildCsv, compute, defaultState, loadInitialState, num, saveState, uid } from "../../lib/capTableMath";
import { CapTableHero, CapTableInstructions } from "./CapTableStatic";
import { CommitInput, FoundersTable, RoundsTable, SafesTable, SummarySection } from "./CapTableSections";
function Section({ id, title, hint, action, children }) {
  return (
    <section className="section" id={id} data-testid={`cap-section-${id}`}>
      <div className="section-head">
        <h2>{title}</h2>
        {action || <span className="hint">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

const AddBtn = ({ label, testid, onClick, ro }) => (
  <button className="btn btn-ghost" onClick={onClick} disabled={ro} data-testid={testid}>{label}</button>
);

function downloadCsv(state) {
  const blob = new Blob([buildCsv(state)], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (state.companyName || "cap-table").replace(/\s+/g, "-").toLowerCase() + ".csv";
  a.click();
  URL.revokeObjectURL(url);
}

// Native Cap Table Modeler. Exposes the same bridge the iframe used
// (cpgCollectState / cpgApplyState / cpgSetViewOnly) so saved Projects
// keep their `cap-table-1` snapshot format.
const CapTableTool = forwardRef(function CapTableTool({ onSave }, ref) {
  const stRef = useRef(null);
  if (!stRef.current) stRef.current = loadInitialState();
  const state = stRef.current;
  const [tick, setTick] = useState(0);
  const [hydrateKey, setHydrateKey] = useState(0);
  const [ro, setRo] = useState(false);

  const update = useCallback((fn) => { fn(stRef.current); setTick((t) => t + 1); }, []);
  const computed = compute(state);
  useEffect(() => { saveState(stRef.current); }, [tick, hydrateKey]);

  useImperativeHandle(ref, () => ({
    cpgCollectState: () => ({ __schema: "cap-table-1", state: JSON.parse(JSON.stringify(stRef.current)) }),
    cpgApplyState: (snap) => {
      if (!snap || typeof snap !== "object") return;
      stRef.current = Object.assign(defaultState(), snap.state || snap);
      setHydrateKey((k) => k + 1);
      setTick((t) => t + 1);
    },
    cpgSetViewOnly: (v) => setRo(!!v),
  }), []);

  const resetAll = () => {
    if (!window.confirm("Reset the entire cap table? Your data will be cleared.")) return;
    localStorage.removeItem("cpg-cap-table-v1");
    stRef.current = defaultState();
    setHydrateKey((k) => k + 1);
    setTick((t) => t + 1);
  };
  const fire = (verb) => { if (!ro) onSave?.(verb); };
  const avail = Math.max(0, state.poolSize - state.poolAllocated);

  return (
    <div className={`ct-native cpg-tool${ro ? " cpg-view-only" : ""}`} data-testid="cap-table-native">
      <div className="container">
        <CapTableHero />
        <CapTableInstructions />
        <Section id="setup" title="1. Company setup" hint="Set the ledger's baseline">
          <div className="callout"><strong>Rule of thumb:</strong> Delaware C-Corp founders typically authorise 10,000,000 shares at incorporation. It has no tax or dilution consequences — it just gives you room to grow.</div>
          <div className="grid-2" key={hydrateKey}>
            <div>
              <label htmlFor="companyName">Company name</label>
              <input id="companyName" type="text" placeholder="e.g. Sunrise Oat Co." defaultValue={state.companyName} disabled={ro} data-testid="cap-company-name"
                onInput={(e) => { stRef.current.companyName = e.target.value; saveState(stRef.current); }} />
            </div>
            <div>
              <label htmlFor="authorizedShares">Total authorised shares</label>
              <input id="authorizedShares" type="number" min="1000000" defaultValue={state.authorizedShares} disabled={ro} data-testid="cap-authorized-shares"
                onInput={(e) => { stRef.current.authorizedShares = num(e.target.value); saveState(stRef.current); }} />
            </div>
          </div>
        </Section>
        <Section id="founders" title="2. Founders" action={<AddBtn label="+ Add founder" testid="cap-add-founder" ro={ro}
          onClick={() => update((s) => s.founders.push({ id: uid(), name: "", role: "Co-founder", shares: 4_000_000 }))} />}>
          <div className="callout"><strong>How this works:</strong> Every founder is granted a fixed number of shares at incorporation. The <em>total</em> founder shares determines the starting cap of the company (before options and investors).</div>
          <FoundersTable state={state} tick={tick} update={update} ro={ro} />
        </Section>
        <Section id="pool" title="3. Employee option pool" hint="Reserved for hires + advisors">
          <div className="callout"><strong>Investor tip:</strong> Most Seed and Series A term sheets require a <em>post-money</em> option pool of 10-15%. Sizing your pool <em>before</em> the round means existing holders (you) take the dilution instead of the new investor. Model both to see the difference.</div>
          <div className="grid-3">
            <div>
              <label htmlFor="poolSize">Pool size (% of fully-diluted, pre-money)</label>
              <input id="poolSize" type="number" min="0" max="40" step="0.5" value={state.poolSize} disabled={ro} data-testid="cap-pool-size"
                onChange={(e) => update((s) => { s.poolSize = num(e.target.value); })} />
            </div>
            <div>
              <label htmlFor="poolAllocated">Already granted (%)</label>
              <input id="poolAllocated" type="number" min="0" max="40" step="0.5" value={state.poolAllocated} disabled={ro} data-testid="cap-pool-allocated"
                onChange={(e) => update((s) => { s.poolAllocated = num(e.target.value); })} />
            </div>
            <div>
              <label htmlFor="poolAvailable">Available for future hires</label>
              <input id="poolAvailable" type="text" disabled value={avail.toFixed(1) + "%"} readOnly data-testid="cap-pool-available" />
            </div>
          </div>
        </Section>
        <Section id="safes" title="4. SAFEs & convertibles" action={<AddBtn label="+ Add SAFE" testid="cap-add-safe" ro={ro}
          onClick={() => update((s) => s.safes.push({ id: uid(), investor: "", amount: 100000, cap: 5000000, discount: 0, converted: false }))} />}>
          <div className="callout"><strong>How these convert:</strong> Every open SAFE converts at your <em>first</em> priced round. The tool computes the conversion price as <code>min(cap / (pre-money shares), priced-round price × (1 − discount))</code> and issues new shares at that price. If you have no priced round yet, the SAFE holder's ownership shows as an estimate at cap.</div>
          <SafesTable state={state} tick={tick} update={update} ro={ro} />
        </Section>
        <Section id="rounds" title="5. Priced rounds" action={<AddBtn label="+ Add round" testid="cap-add-round" ro={ro}
          onClick={() => update((s) => s.rounds.push({ id: uid(), name: `Seed ${s.rounds.length + 1}`, amount: 1_000_000, preMoney: 8_000_000 }))} />}>
          <div className="callout callout-teal"><strong>Math you need to know:</strong> When you add $X at a pre-money of $Y, the price per share is $Y ÷ pre-money-shares. The investor's ownership is $X ÷ ($X + $Y). Existing holders keep their share count but the total share count grows, so their <em>percentage</em> falls.</div>
          <RoundsTable state={state} computed={computed} tick={tick} update={update} ro={ro} />
        </Section>
        <Section id="summary" title="6. Fully-diluted ownership" hint="The number your next investor will see">
          <SummarySection computed={computed} />
        </Section>
        <div className="footer-actions no-print">
          <button className="btn btn-save cpg-save-btn" data-testid="tool-save-draft" disabled={ro} onClick={() => fire("save-draft")}>Save Draft to Projects</button>
          <button className="btn btn-save cpg-save-btn" data-testid="tool-save-result" disabled={ro} onClick={() => fire("save-result")}>Save Result to Projects</button>
          <button className="btn btn-ghost" onClick={() => downloadCsv(stRef.current)} data-testid="cap-export-csv">Download CSV</button>
          <button className="btn btn-ghost" onClick={() => window.print()} data-testid="cap-print">Print / save PDF</button>
          <button className="btn btn-ghost" onClick={resetAll} disabled={ro} data-testid="cap-reset">Reset</button>
        </div>
      </div>
    </div>
  );
});

export default CapTableTool;
